import React, { useState } from 'react';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { MockApolloClient } from 'mock-apollo-client';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Modal } from '@modernhealth/vital-components';

import { CoachingVisitState } from '../../../../../generated/webapp_gql';
import { getCurrentUser } from '../../../../../selectors/userSelectors';
import PaymentReminder from '../../PaymentReminder/PaymentReminder';
import ScheduleSessionConfirmation from '../../ScheduleSessionConfirmation';
import SchedulerForm from '../SchedulerForm';
import { SessionDetails } from '../../ScheduleSessionForm/constants';
import { useCancelEventMutation } from '../../../SessionsList/SessionsByDay/SessionRow/documents/CancelEventMutation.generated';
import { SessionSchedulerModalTestID } from './constants';
interface Props {
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  onClose: () => void;
  isVisible: boolean;
}
const SessionSchedulerModal: React.FC<Props> = ({ memberClient, onClose, isVisible }) => {
  const [sessionDetails, setSessionDetails] = useState<SessionDetails>();
  const [hasSentReminderSuccessfully, setHasSentReminderSuccessfully] = useState<boolean>(false);
  const snackbar = useSnackbar();
  const history = useHistory();
  const currentUser = useSelector(getCurrentUser);
  const { showHpiClientStatusInfo } = useFlags();
  const handleSuccess = (newSessionDetails) => setSessionDetails(newSessionDetails);
  const [cancelEventMutation, { loading: isCanceling }] = useCancelEventMutation({
    client: memberClient,
    onCompleted: (data) => {
      if (data?.cancelEvent?.ok) {
        onClose();
      } else {
        snackbar.enqueueSnackbar('Could not cancel event', { variant: 'error' });
      }
    },
    onError: () => {
      snackbar.enqueueSnackbar('Could not cancel event', { variant: 'error' });
    },
  });
  const canShowReminderModal =
    showHpiClientStatusInfo &&
    currentUser.isTherapist &&
    sessionDetails?.member?.isCompanyHpiEnabled;
  const canShowPaymentReminder =
    canShowReminderModal &&
    (sessionDetails?.sessionState === CoachingVisitState.PendingPayment ||
      sessionDetails?.sessionState === CoachingVisitState.PendingConfirmation);
  const canShowConfirmationModal = !(canShowReminderModal && canShowPaymentReminder);
  const handleClose = () => {
    if (sessionDetails) {
      history.go(0);
    }
    onClose();
  };
  const handleCancel = () => {
    if (
      sessionDetails?.sessionState === CoachingVisitState.Scheduled ||
      hasSentReminderSuccessfully ||
      !canShowReminderModal
    ) {
      handleClose();
      return;
    }
    if (!(sessionDetails && canShowPaymentReminder)) return;
    cancelEventMutation({
      variables: {
        uuid: sessionDetails.sessionUuid,
        enqueueVisitCancelledEmailEvent: false,
      },
    });
    onClose();
  };
  const handleReminderSent = () => {
    setHasSentReminderSuccessfully(true);
  };
  return (
    <Modal isVisible={isVisible} onCloseButtonClick={handleCancel} noPaddings maxWidth={650}>
      <div
        data-testid={SessionSchedulerModalTestID.ScheduleSessionForm}
        className="flex flex-col justify-center items-center mb-4 mx-6 mt-12"
      >
        {sessionDetails && canShowConfirmationModal && (
          <ScheduleSessionConfirmation sessionDetails={sessionDetails} />
        )}
        {!sessionDetails && (
          <SchedulerForm memberClient={memberClient} onCancel={onClose} onSuccess={handleSuccess} />
        )}
        {sessionDetails && canShowPaymentReminder && (
          <PaymentReminder
            isCanceling={isCanceling}
            memberClient={memberClient}
            onCancel={handleCancel}
            onSuccess={handleReminderSent}
            sessionDetails={sessionDetails}
          />
        )}
      </div>
    </Modal>
  );
};
export default SessionSchedulerModal;
